import { useState } from 'react';
import { useAppDispatch } from '../../../hooks/reduxHooks';
import {
    useGetVisitasIndicatorsQuery,
    useDownloadVisitaActaMutation,
    useLazyGetAllVisitsQuery,
    useLazyGetRejectedVisitsQuery,
    useLazyGetPendingVisitsQuery,
    useLazyGetProgrammedVisitsQuery,
    useLazyGetClosedVisitsQuery,
    useLazyGetClosedVisitsBOQuery,
    useLazyGetInProgressVisitsQuery,
    useLazyGetInRevisionVisitsQuery,
    useExportVisitasMutation,
    useDownloadTechnicalVisitPdfMutation,
    useDownloadSeguimientoVerosimilitudPdfMutation
} from '../../../services/visitasApi';
import { Visita } from '../consts/visitasTypes';
import { useRoles } from '../../../hooks/useRoles';
import { showAlert } from '../../../features/slices/applicationSlice';
import { useTranslation } from 'react-i18next';
import { useLazyGetPresentacionRGRLPDFbyIdQuery } from '../../../services/presentacionesApi';
import { useSnackbar } from 'notistack';
import useTableTabs from './useTableTabs';
import useVisitsSelection from './useVisitsSelection';
import { DrawerAction } from '../components/drawers/DrawerManager';
import { buildActaInterviewedData } from '../utils/actaPayload';
import useServerExcelExport from '../../../utils/hooks/useServerExcelExport';
import { armarFiltrosExportacion } from '../utils/filtrosExportacion';

const useVisitas = ({
    filters,
    extraFilters,
    setFilters,
    isHome
}: {
    filters: any;
    extraFilters: any;
    setFilters: any;
    isHome?: boolean;
}) => {
    const dispatch = useAppDispatch();
    const { isPreventor } = useRoles();
    const { t } = useTranslation('visitas');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [selected, setSelected] = useState<Visita | null>(null);
    const [openCard, setOpenCard] = useState<boolean>(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerAction, setDrawerAction] = useState<DrawerAction | null>(null);
    const [selectedVisita, setSelectedVisita] = useState<Visita | null>(null);
    const [downloadVisitaActa] = useDownloadVisitaActaMutation();
    const [downloadTechnicalVisitPdf] = useDownloadTechnicalVisitPdfMutation();
    const [downloadSeguimientoVerosimilitudPdf] = useDownloadSeguimientoVerosimilitudPdfMutation();
    const [getPresentacionRGRLPDFbyId] = useLazyGetPresentacionRGRLPDFbyIdQuery();
    const [exportVisitas, { isLoading: isExportingVisitas }] = useExportVisitasMutation();
    const { exportToExcel } = useServerExcelExport(exportVisitas);
    const { enqueueSnackbar } = useSnackbar();

    const ENDPOINTS_BY_TAB = isPreventor
        ? [
              useLazyGetPendingVisitsQuery, //funcion repetida para no perder el orden de los tabs, TODO: buscar mejor solucion
              useLazyGetPendingVisitsQuery,
              useLazyGetPendingVisitsQuery,
              useLazyGetProgrammedVisitsQuery,
              useLazyGetInProgressVisitsQuery,
              useLazyGetInRevisionVisitsQuery,
              useLazyGetClosedVisitsQuery
          ]
        : [
              useLazyGetAllVisitsQuery,
              useLazyGetRejectedVisitsQuery,
              useLazyGetPendingVisitsQuery,
              useLazyGetProgrammedVisitsQuery,
              useLazyGetInProgressVisitsQuery,
              useLazyGetInRevisionVisitsQuery,
              useLazyGetClosedVisitsBOQuery
          ];

    const { currentTabIndex, handleTabChange, result } = useTableTabs({
        ENDPOINTS_BY_TAB: ENDPOINTS_BY_TAB,
        filters: filters,
        extraFilters: extraFilters
    });

    const isFetchingVisits = result.isFetching ?? true;

    const visibleVisits = result?.data?.data?.data ?? [];
    const {
        selectedVisitsIds,
        isChecked,
        isAllChecked,
        handleAllCheckPress,
        handleCheckPress,
        clearVisitsSelection
    } = useVisitsSelection(visibleVisits);

    const handleTabClick = (e: React.SyntheticEvent, newValue: number) => {
        clearVisitsSelection();
        setFilters({ ...filters, skip: 0 });
        handleTabChange(e, newValue);
    };

    const handleCancelMasiveAssignationClick = () => {
        clearVisitsSelection();
    };

    const {
        data: indicatorsData,
        isFetching: isFetchingIndicators,
        refetch: refetchIndicators
    } = useGetVisitasIndicatorsQuery({});

    const handleClickAction = (event: any, item: Visita) => {
        setSelected(item);
        setAnchorEl(event.currentTarget);
        setMenuOpen(true);
    };

    const handleSelectRow = (item: Visita) => {
        setSelected(item);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setMenuOpen(false);
    };

    const handleCloseCard = () => {
        setOpenCard(false);
        setSelected(null);
    };

    const handleShowVisitaData = () => {
        setOpenCard(true);
        setMenuOpen(false);
    };

    const handleOpenDrawer = (action: DrawerAction, visita: Visita | null) => {
        setDrawerAction(action);
        setSelectedVisita(visita);
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setDrawerAction(null);
        setSelectedVisita(null);
    };
    // Acta de la visita. El acta de cada falla se baja desde el drawer de
    // useActaFallida (SMP-599).
    const handleDownloadActa = async () => {
        try {
            if (!selected) return;

            dispatch(
                showAlert({
                    icon: 'info',
                    message: t('alerts.downloadActa.generating'),
                    loading: true
                })
            );

            const response = await downloadVisitaActa({
                idVisita: selected.idVisita || selected.id,
                idTipoVisita:
                    selected?.idTipoVisita ??
                    (selected as any)?.actividades?.[0]?.tipoVisita?.id,
                tipoActa: 1,
                datos: buildActaInterviewedData(selected)
            }).unwrap();

            if (response?.data) {
                const byteCharacters = atob(response.data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);

                const blob = new Blob([byteArray], { type: 'application/pdf' });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                const fileName = `${t('table.actions.downloadActaFilename')}.pdf`;
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                dispatch({ type: 'application/hideAlert' });

                enqueueSnackbar(t('alerts.downloadActa.success'), { variant: 'success' });
            }
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });

            let errorMessage = t('alerts.downloadActa.error');

            try {
                if (error?.data instanceof Blob) {
                    const text = await error.data.text();
                    const json = JSON.parse(text);
                    errorMessage = json?.message || errorMessage;
                } else if (error?.data?.message) {
                    errorMessage = error.data.message;
                } else if (error?.error?.data?.message) {
                    errorMessage = error.error.data.message;
                } else if (error?.error) {
                    errorMessage = error.error;
                }
            } catch (parseError) {}

            dispatch(
                showAlert({
                    icon: 'error',
                    message: errorMessage
                })
            );
        }
    };

    const handleDownloadVerosimilitud = async () => {
        try {
            if (!selected) return;

            dispatch(
                showAlert({
                    icon: 'info',
                    message: t('alerts.downloadVerosimilitud.generating'),
                    loading: true
                })
            );

            const response = await downloadVisitaActa({
                idVisita: selected.idVisita || selected.id,
                idTipoVisita:
                    selected?.idTipoVisita ??
                    (selected as any)?.actividades?.[0]?.tipoVisita?.id,
                tipoActa: 2
            }).unwrap();

            if (response?.data) {
                const byteCharacters = atob(response.data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);

                const blob = new Blob([byteArray], { type: 'application/pdf' });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                const fileName = `${t('table.actions.downloadVerosimilitudFilename')}.pdf`;
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                dispatch({ type: 'application/hideAlert' });

                enqueueSnackbar(t('alerts.downloadVerosimilitud.success'), { variant: 'success' });
            }
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });

            let errorMessage = t('alerts.downloadVerosimilitud.error');

            try {
                if (error?.data instanceof Blob) {
                    const text = await error.data.text();
                    const json = JSON.parse(text);
                    errorMessage = json?.message || errorMessage;
                } else if (error?.data?.message) {
                    errorMessage = error.data.message;
                } else if (error?.error?.data?.message) {
                    errorMessage = error.error.data.message;
                } else if (error?.error) {
                    errorMessage = error.error;
                }
            } catch (parseError) {}

            dispatch(
                showAlert({
                    icon: 'error',
                    message: errorMessage
                })
            );
        }
    };

    const handleDownloadSeguimientoVerosimilitud = async () => {
        try {
            if (!selected) return;

            dispatch(
                showAlert({
                    icon: 'info',
                    message: t('alerts.downloadVerosimilitud.generating'),
                    loading: true
                })
            );

            const response = await downloadSeguimientoVerosimilitudPdf({
                idActividad: selected.idVisita || selected.id
            }).unwrap();

            if (response?.data) {
                const byteCharacters = atob(response.data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Seguimiento-Verosimilitud.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                dispatch({ type: 'application/hideAlert' });
                enqueueSnackbar(t('alerts.downloadVerosimilitud.success'), { variant: 'success' });
            }
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });
            let errorMessage = t('alerts.downloadVerosimilitud.error');
            try {
                if (error?.data instanceof Blob) {
                    const text = await error.data.text();
                    const json = JSON.parse(text);
                    errorMessage = json?.message || errorMessage;
                } else if (error?.data?.message) {
                    errorMessage = error.data.message;
                }
            } catch {}
            dispatch(showAlert({ icon: 'error', message: errorMessage }));
        }
    };

    const handleDownloadVisitaTecnica = async () => {
        try {
            if (!selected) return;

            dispatch(
                showAlert({
                    icon: 'info',
                    message: t('alerts.downloadVisitaTecnica.generating'),
                    loading: true
                })
            );

            const response = await downloadTechnicalVisitPdf({
                idVisita: selected.idVisita || selected.id
            }).unwrap();

            const responseData =
                typeof response === 'string' ? response : (response as { data?: string })?.data;

            if (responseData) {
                const byteCharacters = atob(responseData);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);

                const blob = new Blob([byteArray], { type: 'application/pdf' });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                const fileName = `${t('table.actions.downloadVisitaTecnicaFilename')}.pdf`;
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                dispatch({ type: 'application/hideAlert' });

                enqueueSnackbar(t('alerts.downloadVisitaTecnica.success'), { variant: 'success' });
            }
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });

            let errorMessage = t('alerts.downloadVisitaTecnica.error');

            try {
                if (error?.data instanceof Blob) {
                    const text = await error.data.text();
                    const json = JSON.parse(text);
                    errorMessage = json?.message || errorMessage;
                } else if (error?.data?.message) {
                    errorMessage = error.data.message;
                } else if (error?.error?.data?.message) {
                    errorMessage = error.error.data.message;
                } else if (error?.error) {
                    errorMessage = error.error;
                }
            } catch (parseError) {}

            dispatch(
                showAlert({
                    icon: 'error',
                    message: errorMessage
                })
            );
        }
    };

    const handleDownloadRgrl = async () => {
        try {
            if (!selected || !selected?.rgrl?.id) {
                dispatch(
                    showAlert({
                        icon: 'error',
                        message: t('alerts.downloadRgrl.notFoundId')
                    })
                );
                return;
            }

            dispatch(
                showAlert({
                    icon: 'info',
                    message: t('alerts.downloadRgrl.generating'),
                    loading: true
                })
            );

            const response = await getPresentacionRGRLPDFbyId({
                idPresentacion: selected?.idPresentacion || selected?.rgrl?.id,
                typeId: 2
            }).unwrap();

            if (response?.data?.presignedUrls?.length > 0) {
                const url = response.data.presignedUrls[0];
                window.open(url, '_blank');

                dispatch({ type: 'application/hideAlert' });

                enqueueSnackbar(t('alerts.downloadRgrl.success'), { variant: 'success' });
            } else {
                throw new Error('No se recibió el archivo.');
            }
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });

            dispatch(
                showAlert({
                    icon: 'error',
                    message: error?.message || t('alerts.downloadRgrl.error')
                })
            );
        }
    };

    const handleExportVisitas = async () => {
        const body = armarFiltrosExportacion(currentTabIndex, filters, extraFilters);

        await exportToExcel(body, 'Visitas');
    };

    return {
        selected,
        indicatorsData,
        isFetchingIndicators,
        openCard,
        anchorEl,
        menuOpen,
        handleClickAction,
        handleSelectRow,
        handleCloseMenu,
        handleCloseCard,
        handleShowVisitaData,
        drawerOpen,
        drawerAction,
        handleOpenDrawer,
        handleCloseDrawer,
        selectedVisita,
        setDrawerOpen,
        refetchIndicators,
        handleDownloadVerosimilitud,
        handleDownloadSeguimientoVerosimilitud,
        handleDownloadActa,
        handleDownloadVisitaTecnica,
        handleDownloadRgrl,
        currentTabIndex,
        handleTabClick,
        isFetchingVisits,
        result,
        handleExportVisitas,
        isExportingVisitas,
        visitSelectionHandler: {
            isChecked,
            handleAllCheckPress,
            handleCheckPress,
            isAllChecked,
            selectedVisitsIds,
            handleCancelMasiveAssignationClick,
            clearVisitsSelection
        }
    };
};

export default useVisitas;
